import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/containers/global/useSession";
import { cn } from "@/lib/utils";
import { appRoutes, navRoutes, RouteNames } from "@/shared/constants/routes";
import { useTranslation } from "@/shared/i18n";
import { CopyValue } from "./CopyValue";
import { initialsOf, shortenAddress } from "@/shared/utils/format";
import * as React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

export type HeaderTone = "light" | "dark";

export interface SiteHeaderProps {
  /**
   * `light` = white type, for a header sitting over full-bleed photography.
   * `dark` = ink type, for pages that start on a cream or white band.
   */
  tone?: HeaderTone;
  /**
   * Let the page show through instead of painting a white bar.
   *
   * Set whenever the page bleeds under the header (`PageLayout heroBleed`).
   * Without it a `dark`-toned header covers the hero's top ~104px — which on
   * the landing page sliced the top clean off the artichokes hanging from the
   * edge. `light` never needed this because it has no background to begin with.
   */
  transparent?: boolean;
}

/**
 * The header is part of the document flow but the page is pulled up under it
 * with `.kf-header-bleed` (see PageLayout), so hero photography runs behind
 * the nav — that is how kuebefarm.lu is built. It does not stick; scrolling
 * leaves it behind, which keeps the reading area undisturbed.
 */
const Logo = ({ tone }: { tone: HeaderTone }) => {
  const { t } = useTranslation();
  return (
    <Link
      to={appRoutes[RouteNames.landing].path}
      className={cn(
        "font-heading text-[15px] font-medium uppercase tracking-logo transition-opacity duration-200 hover:opacity-70 sm:text-[20px]",
        tone === "light" ? "text-kf-white" : "text-kf-ink",
      )}
      aria-label={t("common.brand")}
    >
      {t("common.brand")}
    </Link>
  );
};

const AccountControl = ({ tone }: { tone: HeaderTone }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading, login, logout } = useSession();

  // Never render the signed-out state while Web3Auth is still rehydrating —
  // v10 restores asynchronously, and flashing "Log in" at a signed-in user on
  // every refresh is exactly the bug this guard exists to prevent.
  if (isLoading) {
    return <Skeleton className="h-4 w-20 bg-current opacity-15" />;
  }

  if (!isAuthenticated || !user) {
    return (
      <Button
        variant="link"
        // `md` is 13px, matching the nav links this sits beside.
        size="md"
        onClick={() => void login()}
        className={tone === "light" ? "text-kf-white" : "text-kf-ink"}
      >
        {t("nav.login")}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("nav.accountMenu")}
        className={cn(
          "flex size-9 items-center justify-center border font-heading text-[10px] font-medium uppercase tracking-[0.08em] transition-colors duration-200 focus-visible:outline-none",
          tone === "light"
            ? "border-kf-white/40 text-kf-white hover:border-kf-white"
            : "border-kf-line text-kf-ink hover:border-kf-ink",
        )}
      >
        {initialsOf(user.name, user.email)}
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <div className="border-b border-kf-line px-5 py-4">
          <p className="font-heading text-[9px] uppercase tracking-wide2 text-kf-muted">
            {t("nav.signedInAs")}
          </p>
          <p className="mt-1.5 truncate text-[13px] font-light text-kf-ink">
            {user.email || user.name}
          </p>
          {/* Copyable, not just readable: the address is what a buyer is asked
              for anywhere their parcel is checked against the ledger, and
              retyping 42 hex characters off a dropdown is how it goes wrong. */}
          {user.walletAddress ? (
            <div className="mt-2 flex items-center gap-2 font-heading text-[9px] uppercase tracking-wide2 text-kf-muted">
              <span>{t("nav.wallet")}</span>
              <span aria-hidden>·</span>
              <CopyValue
                value={user.walletAddress}
                labelKey="nav.copyWallet"
                display={shortenAddress(user.walletAddress)}
              />
            </div>
          ) : null}
        </div>

        <DropdownMenuItem asChild>
          <Link to={appRoutes[RouteNames.myFarm].path}>{t("nav.myFarm")}</Link>
        </DropdownMenuItem>

        {user.isAdmin ? (
          <DropdownMenuItem asChild>
            <Link to={appRoutes[RouteNames.admin].path}>{t("nav.admin")}</Link>
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => void logout()}>
          {t("nav.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MobileMenu = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, login, logout } = useSession();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-kf-cream lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={t("nav.openMenu")}
    >
      <div className="flex h-[var(--kf-header-h)] shrink-0 items-center justify-between px-6">
        <span className="font-heading text-[15px] font-medium uppercase tracking-logo text-kf-ink">
          {t("common.brand")}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("nav.closeMenu")}
          className="relative size-9 text-kf-ink"
        >
          <span className="absolute left-1/2 top-1/2 h-px w-5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-current" />
          <span className="absolute left-1/2 top-1/2 h-px w-5 -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-current" />
        </button>
      </div>

      <nav className="flex flex-1 flex-col justify-center gap-2 px-6 pb-24">
        {navRoutes.map((route, index) => (
          <NavLink
            key={route.path}
            to={route.path}
            onClick={onClose}
            end={route.path === "/"}
            className="animate-fade-up border-b border-kf-taupe/60 py-6 font-heading text-[22px] font-medium uppercase tracking-brand text-kf-ink"
            style={{ animationDelay: `${index * 60}ms` }}
          >
            {t(route.labelKey)}
          </NavLink>
        ))}

        <div className="mt-10">
          <Button
            size="md"
            onClick={() => {
              onClose();
              void (isAuthenticated ? logout() : login());
            }}
          >
            {isAuthenticated ? t("nav.logout") : t("nav.login")}
          </Button>
        </div>
      </nav>
    </div>
  );
};

const SiteHeader = ({
  tone = "dark",
  transparent = false,
}: SiteHeaderProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <>
      <a
        href="#main"
        className={cn(
          "sr-only focus:not-sr-only focus:absolute focus:left-6 focus:top-6 focus:z-[70]",
          "focus:border focus:border-kf-ink focus:bg-kf-white focus:px-4 focus:py-2",
          "focus:font-heading focus:text-[11px] focus:uppercase focus:tracking-wide2 focus:text-kf-ink",
        )}
      >
        {t("common.skipToContent")}
      </a>

      <header
        className={cn(
          "relative z-50 h-[var(--kf-header-h)] w-full",
          tone === "dark" && !transparent && "bg-kf-white",
        )}
      >
        <div className="container flex h-full items-center justify-between gap-8">
          <Logo tone={tone} />

          <div className="flex items-center gap-8">
            <nav
              className="hidden items-center gap-9 lg:flex"
              aria-label={t("common.brand")}
            >
              {navRoutes.map((route) => (
                <NavLink
                  key={route.path}
                  to={route.path}
                  end={route.path === "/"}
                  className={({ isActive }) =>
                    cn(
                      "kf-underline font-heading text-[13px] font-medium uppercase tracking-wide2 transition-colors duration-200",
                      tone === "light"
                        ? "text-kf-white/85 hover:text-kf-white"
                        : "text-kf-secondary hover:text-kf-ink",
                      isActive &&
                        cn(
                          "kf-underline--on",
                          tone === "light" ? "text-kf-white" : "text-kf-ink",
                        ),
                    )
                  }
                >
                  {t(route.labelKey)}
                </NavLink>
              ))}
            </nav>

            <span
              aria-hidden
              className={cn(
                "hidden h-4 w-px lg:block",
                tone === "light" ? "bg-kf-white/30" : "bg-kf-line",
              )}
            />

            <AccountControl tone={tone} />

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label={t("nav.openMenu")}
              aria-expanded={menuOpen}
              className={cn(
                "relative size-9 lg:hidden",
                tone === "light" ? "text-kf-white" : "text-kf-ink",
              )}
            >
              <span className="absolute left-1/2 top-1/2 h-px w-5 -translate-x-1/2 -translate-y-[5px] bg-current" />
              <span className="absolute left-1/2 top-1/2 h-px w-5 -translate-x-1/2 translate-y-[4px] bg-current" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

export { SiteHeader };
